import { getReportPrintPageCss } from './reportPrintPage.util'

// Copy the already rendered pages instead of booting another app and fetching
// the report again. A hidden report route may never satisfy its auto-print gate.
export const printRenderedReport = ({ source, layout, onPrepared, onError }) => {
  if (!source?.querySelector('.print-page-sheet')) {
    onError(new Error('ไม่มีหน้ารายงานพร้อมพิมพ์'))
    return () => {}
  }

  const sourceDocument = source.ownerDocument
  const frame = sourceDocument.createElement('iframe')
  let disposed = false
  let started = false
  let timeout
  const assetCleanups = []
  const dispose = () => {
    if (disposed) return
    disposed = true
    window.clearTimeout(timeout)
    assetCleanups.forEach((cleanup) => cleanup())
    frame.remove()
  }
  const fail = (error) => {
    if (disposed) return
    dispose()
    onError(error)
  }
  const waitForAsset = (element) => new Promise((resolve, reject) => {
    const removeListeners = () => {
      element.removeEventListener('load', loaded)
      element.removeEventListener('error', failed)
    }
    const loaded = () => { removeListeners(); resolve() }
    const failed = () => {
      removeListeners()
      reject(new Error('โหลดรูปภาพหรือรูปแบบหน้าพิมพ์ไม่สำเร็จ'))
    }
    assetCleanups.push(removeListeners)
    element.addEventListener('load', loaded, { once: true })
    element.addEventListener('error', failed, { once: true })
  })

  frame.title = 'report-print-frame'
  Object.assign(frame.style, {
    position: 'fixed', left: '-10000px', top: '0',
    width: '1400px', height: '1600px', border: '0',
    pointerEvents: 'none',
  })
  frame.onload = async () => {
    if (started || disposed) return
    started = true
    try {
      const printDocument = frame.contentDocument
      const printWindow = frame.contentWindow
      if (!printDocument || !printWindow) throw new Error('เปิดหน้าพิมพ์ไม่สำเร็จ')
      printDocument.title = sourceDocument.title
      printDocument.documentElement.lang = sourceDocument.documentElement.lang
      const base = printDocument.createElement('base')
      base.href = sourceDocument.baseURI
      printDocument.head.appendChild(base)
      const assets = []
      sourceDocument.querySelectorAll('style, link[rel="stylesheet"]').forEach((style) => {
        if (style.hasAttribute('data-report-page-style')) return
        const copy = style.cloneNode(true)
        if (copy.tagName === 'LINK') assets.push(waitForAsset(copy))
        printDocument.head.appendChild(copy)
      })
      const pageStyle = printDocument.createElement('style')
      pageStyle.media = 'print'
      pageStyle.textContent = getReportPrintPageCss(layout, true)
      printDocument.head.appendChild(pageStyle)
      printDocument.body.className = sourceDocument.body.className
      const report = printDocument.createElement('div')
      report.className = 'print-report'
      const pages = source.cloneNode(true)
      pages.classList.add('auto-print')
      // cloneNode does not copy a canvas bitmap (e.g. a barcode).
      const copiedCanvases = pages.querySelectorAll('canvas')
      source.querySelectorAll('canvas').forEach((canvas, index) => {
        copiedCanvases[index]?.getContext('2d')?.drawImage(canvas, 0, 0)
      })
      pages.querySelectorAll('.report-flow-measure, script').forEach((node) => node.remove())
      pages.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager'
        if (!img.complete) assets.push(waitForAsset(img))
        else if (!img.naturalWidth) assets.push(Promise.reject(new Error('โหลดรูปภาพรายงานไม่สำเร็จ')))
      })
      report.appendChild(pages)
      printDocument.body.appendChild(report)
      await Promise.all(assets)
      // Start font loading after the pages have been laid out. Do not rely on
      // requestAnimationFrame in an offscreen iframe: Chrome may throttle it.
      printDocument.body.getBoundingClientRect()
      if (printDocument.fonts?.ready) await printDocument.fonts.ready
      if (disposed) return
      window.clearTimeout(timeout)
      printWindow.addEventListener('afterprint', dispose, { once: true })
      onPrepared()
      printWindow.focus()
      printWindow.print()
      if (!disposed) timeout = window.setTimeout(dispose, 300000)
    } catch (error) {
      fail(error)
    }
  }
  frame.onerror = () => fail(new Error('เปิดหน้าพิมพ์ไม่สำเร็จ'))
  frame.srcdoc = '<!doctype html><html><head></head><body></body></html>'
  timeout = window.setTimeout(() => fail(new Error('เตรียมหน้าพิมพ์เกินเวลา กรุณาลองอีกครั้ง')), 30000)
  sourceDocument.body.appendChild(frame)
  return dispose
}
